/**
 * @file use-configurations.ts
 * @module app/modules/setting/hooks/use-configurations
 * @description Data and behavior hook for the Configurations tab: fetches
 * settings, tracks per-row warehouse selection, and persists updates.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.5
 * @version 2.1.2
 */

import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { platformSettings } from '../../../../api/apiEndPoints';
import { error, success } from '../../../../Global/toast';
import { POSTHOG_ENABLED_CACHE_KEY, Settings as SettingsPermission } from '../../../../utils/constants';
import { CONFIGURATION_SETTING_KEYS } from '../../../../utils/configurationSettingKeys';
import { callApiWithToast } from '../../../../utils/apiCallWithToast';
import { useWarehouse } from '../../../context/WarehouseContext';
import { fetchConfigurationRecords } from '../services/settings-service';
import { GROUP_ORDER, GROUP_TITLES, type IFlagItem } from '../types/configurations.types';
import APICallService from '../../../../api/apiCallService';
import { ApiResponseToastItem, showToasterFromApiResponse } from 'src/utils/apiResponseToast';
import { getRecordFromStructuredResponse, showSilentResponseNotification } from "src/app/modules/_common/utils/api-helper";

/** Action settings that first ask the admin for comma-separated order refKeys. */
const REF_KEY_ACTION_PROMPTS: Record<
  string,
  {
    title: string;
    confirmButtonText: string;
    icon: 'question' | 'warning';
    cancelButtonText?: string;
  }
> = {
  [CONFIGURATION_SETTING_KEYS.KEY__FREE_ORDER]: {
    title: 'Free Order',
    confirmButtonText: 'Free Order',
    icon: 'question',
  },
  [CONFIGURATION_SETTING_KEYS.KEY__DELIVER_ORDER]: {
    title: 'Deliver Order',
    confirmButtonText: 'Mark Delivered',
    icon: 'warning',
  },
  [CONFIGURATION_SETTING_KEYS.KEY__CANCEL_ORDER]: {
    title: 'Cancel Order',
    confirmButtonText: 'Cancel Orders',
    icon: 'warning',
    // The default "Cancel" would read like the action itself.
    cancelButtonText: 'Close',
  },
};

/**
 * Loads configuration records and exposes state/handlers for the Configurations tab.
 * @returns {Object} Tab state and action handlers.
 */
export const useConfigurations = () => {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<IFlagItem[]>([]);
  const [values, setValues] = useState<Record<string, any>>({});
  const [updatingKeys, setUpdatingKeys] = useState<Record<string, boolean>>({});
  const [itemWarehouseId, setItemWarehouseId] = useState<Record<string, string>>({});
  const { warehouseList, selectedWarehouse } = useWarehouse();

  useEffect(() => {
    (async () => {
      await fetchSettings();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Fetches configuration records from backend and initializes local state.
   * @returns {Promise<void>} Promise that resolves after state update.
   */
  const fetchSettings = async () => {
    setLoading(true);
    const backendRecords = await fetchConfigurationRecords(selectedWarehouse?.value);

    const initialValues = (backendRecords as IFlagItem[]).reduce((acc, item) => {
      acc[item.key] = item.value;
      return acc;
    }, {} as Record<string, any>);

    const initialWarehouseSelections = (backendRecords as IFlagItem[]).reduce((acc, item) => {
      if (item.isGlobal === false) {
        acc[item.key] = selectedWarehouse?.value || '';
      }
      return acc;
    }, {} as Record<string, string>);

    setRecords(backendRecords as IFlagItem[]);
    setValues(initialValues);
    setItemWarehouseId(initialWarehouseSelections);
    setLoading(false);
  };

  /**
   * Updates local value map for a specific setting key.
   * @param {string} key Setting key.
   * @param {any} value Setting value.
   * @returns {void}
   */
  const updateValue = (key: string, value: any) => {
    setValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /**
   * Toggles per-key loading state while update API is in progress.
   * @param {string} key Setting key.
   * @param {boolean} value Updating state flag.
   * @returns {void}
   */
  const setKeyUpdating = (key: string, value: boolean) => {
    setUpdatingKeys((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /**
   * Executes a configuration action setting by key.
   * @param {IFlagItem} item Action setting item.
   * @returns {Promise<void>} Promise resolved after action call.
   */
  const executeConfigurationAction = async (item: IFlagItem) => {
    setKeyUpdating(item.key, true);

    try {
      let refKeys: string | undefined;
      const refKeyPrompt = REF_KEY_ACTION_PROMPTS[item.key];
      if (refKeyPrompt) {
        const confirmation = await Swal.fire<string>({
          icon: refKeyPrompt.icon,
          title: refKeyPrompt.title,
          text: 'Comma-separated order refKeys',
          input: 'text',
          showCancelButton: true,
          confirmButtonText: refKeyPrompt.confirmButtonText,
          ...(refKeyPrompt.cancelButtonText ? { cancelButtonText: refKeyPrompt.cancelButtonText } : {}),
          inputValidator: (value) =>
            value.split(',').some((refKey) => refKey.trim())
              ? undefined
              : 'Enter at least one order refKey',
        });
        if (!confirmation.isConfirmed || !confirmation.value) {
          return;
        }
        refKeys = confirmation.value;
      }

      const apiResponse = await callApiWithToast({
        endpoint: platformSettings.executeConfigurationAction,
        params: {
          key: item.key,
          ...(refKeys ? { refKeys } : {}),
        },
        module: SettingsPermission,
      });
      const response = getRecordFromStructuredResponse(apiResponse);

      if (response !== null && item.key === CONFIGURATION_SETTING_KEYS.KEY__DOWNLOAD_DEBUG_LOGS_ZIP) {
        const fileName = response?.result?.fileName || 'debug-logs.zip';
        const mimeType = response?.result?.mimeType || 'application/zip';
        const zipBase64 = response?.result?.zipBase64;

        if (!zipBase64) {
          throw new Error('Unable to download logs. ZIP payload is missing.');
        }

        const binary = atob(zipBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }

        const blob = new Blob([bytes], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      if (item.key === CONFIGURATION_SETTING_KEYS.KEY__SYNC_DATABASE_INDEXES
        || item.key === CONFIGURATION_SETTING_KEYS.KEY__SYNC_ALL_DATABASE_INDEXES
      ) {
        const result = response?.result;
        const createdCount = (result?.models || []).reduce(
          (total: number, model: any) => total + (model?.createdIndexes?.length || 0),
          0,
        );
        response.message = `Indexes updated. ${createdCount} new index(es) created across ${result?.syncedModels || 0} collection(s).`
        showToasterFromApiResponse(response as ApiResponseToastItem, showSilentResponseNotification);
        return;
      }
      if (response && item.key === CONFIGURATION_SETTING_KEYS.KEY__DELIVER_ORDER) {
        const updatedRefs: string[] = response?.result?.updatedRefs || [];
        const partiallyDeliveredRefs: string[] = response?.result?.partiallyDeliveredRefs || [];
        // A short-loaded order gets a delivered route entry but keeps its order status.
        response.message = partiallyDeliveredRefs.length
          ? `Marked ${updatedRefs.length} order(s) as delivered. Delivered quantity is below ordered quantity, so order status is unchanged for: ${partiallyDeliveredRefs.join(', ')}`
          : `Marked ${updatedRefs.length} order(s) as delivered.`;
      }
      if (response && item.key === CONFIGURATION_SETTING_KEYS.KEY__CANCEL_ORDER) {
        const updatedRefs: string[] = response?.result?.updatedRefs || [];
        response.message = `Cancelled ${updatedRefs.length} order(s): ${updatedRefs.join(', ')}`;
      }
      showToasterFromApiResponse(response as ApiResponseToastItem, showSilentResponseNotification);
    } catch (e: any) {
      showSilentResponseNotification(
        "error",
        e?.message || "Something went wrong. Please try again.",
      );
    } finally {
      setKeyUpdating(item.key, false);
    }
  };

  /**
   * Handles warehouse selection for a non-global setting row.
   * Refetches the effective value for that key under the chosen warehouse.
   * @param {IFlagItem} item Setting metadata item.
   * @param {string} warehouseId Selected warehouse id; empty clears the selection.
   * @returns {Promise<void>} Promise resolved after the row's value is refreshed.
   */
  const handleItemWarehouseChange = async (item: IFlagItem, warehouseId: string) => {
    setItemWarehouseId((prev) => ({
      ...prev,
      [item.key]: warehouseId,
    }));

    if (!warehouseId) {
      updateValue(item.key, item.defaultValue);
      return;
    }

    const scopedRecords = await fetchConfigurationRecords(warehouseId);
    const scopedItem = (scopedRecords as IFlagItem[]).find((record) => record.key === item.key);

    if (scopedItem) {
      updateValue(item.key, scopedItem.value);
    }
  };

  /**
   * Persists a configuration value by key with optimistic UI update.
   * Rolls back local value on API failure.
   *
   * @param {IFlagItem} item Setting metadata item.
   * @param {any} nextValue New value to save.
   * @returns {Promise<void>} Promise that resolves after save attempt.
   */
  const persistConfigurationValue = async (item: IFlagItem, nextValue: any) => {
    if (item.isGlobal === false && !itemWarehouseId[item.key]) {
      error('Select a warehouse before updating this setting.');
      return;
    }

    const previousValue = values?.[item.key];
    updateValue(item.key, nextValue);
    setKeyUpdating(item.key, true);

    const response = await callApiWithToast({
      endpoint: platformSettings.updateConfigurationSetting,
      params: {
        key: item.key,
        value: nextValue,
      },
      module: SettingsPermission,
      warehouse: item.isGlobal === false ? itemWarehouseId[item.key] : undefined,
    });

    if (!response) {
      updateValue(item.key, previousValue);
      setKeyUpdating(item.key, false);
      return;
    }

    if (item.key === CONFIGURATION_SETTING_KEYS.KEY__ENABLE_POSTHOG) {
      const enabled = Boolean(nextValue);
      localStorage.setItem(POSTHOG_ENABLED_CACHE_KEY, String(enabled));
    }

    const nextRecords = records.map((record) =>
      record.key === item.key ? { ...record, value: nextValue } : record,
    );
    setRecords(nextRecords);
    setKeyUpdating(item.key, false);
  };

  /**
   * Returns records for a given group key.
   * @param {string} groupKey Group identifier.
   * @returns {IFlagItem[]} Matching records.
   */
  const getGroupRecords = (groupKey: string) => {
    return records.filter((item) => (item.group || '').toLowerCase() === groupKey && !item.hide);
  };

  /**
   * Converts a raw group key to a UI title.
   * @param {string} groupKey Group identifier.
   * @returns {string} Human-readable title.
   */
  const buildGroupTitle = (groupKey: string) => {
    if (GROUP_TITLES[groupKey]) return GROUP_TITLES[groupKey];
    return groupKey
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  /**
   * Converts a subgroup key to a readable legend.
   * @param {string} subgroupKey Subgroup identifier.
   * @returns {string} Legend text.
   */
  const buildSubgroupTitle = (subgroupKey: string) => buildGroupTitle(subgroupKey);

  /**
   * Builds the row description, appending the default value line only when
   * a default value is actually set.
   * @param {IFlagItem} item Setting metadata item.
   * @returns {string} Row description text.
   */
  const buildItemDescription = (item: IFlagItem) => {
    const baseDescription = item.description || 'No description available.';

    if (item.type === 'action' || item.control === 'textarea') {
      return baseDescription;
    }

    const defaultValue = String(item.defaultValue ?? '').trim();
    if (!defaultValue) {
      return baseDescription;
    }

    return `${baseDescription}\nDefault value is ${defaultValue.toUpperCase()}.`;
  };

  const groups = Array.from(
    new Set([
      ...GROUP_ORDER,
      ...records
        .map((item) => (item.group || '').toLowerCase())
        .filter(Boolean),
    ]),
  ).map((key) => ({
    key,
    title: buildGroupTitle(key),
  }));

  return {
    loading,
    values,
    updatingKeys,
    itemWarehouseId,
    warehouseList,
    groups,
    getGroupRecords,
    buildSubgroupTitle,
    buildItemDescription,
    executeConfigurationAction,
    handleItemWarehouseChange,
    persistConfigurationValue,
  };
};
